import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Divider,
  Alert,
  CircularProgress,
  Grid,
  Chip,
  RadioGroup,
  Radio,
  FormLabel,
  IconButton,
  Card,
  CardContent,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  alpha,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Calculate as CalculateIcon,
  Info as InfoIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import moment from 'moment-timezone';
import arSA from 'date-fns/locale/ar-SA';
import apiService from '../../../services/apiService';
import { useAuth } from '../../../context/AuthContext';
import { useSpecializedVoucherPermissions } from '../../../hooks/useSpecializedVoucherPermissions';

// واجهة الحساب
interface Account {
  id: string;
  name: string;
  code: string;
  type: string;
  balance: number;
}

// واجهة قالب السند المتخصص
interface SpecializedVoucherTemplate {
  id: string;
  name: string;
  voucherType: 'receipt' | 'payment' | 'journal';
  description?: string;
  isActive: boolean;
  requiresApproval: boolean;
  hasFees: boolean;
  debitAccounts: string[];  // معرفات الحسابات المدينة المتاحة
  creditAccounts: string[]; // معرفات الحسابات الدائنة المتاحة
  feeGroups: string[];      // معرفات مجموعات الرسوم المتاحة
}

// واجهة الرسم
interface Fee {
  id: string;
  name: string;
  type: 'fixed' | 'percentage' | 'tax';
  value: number;  // قيمة ثابتة أو نسبة مئوية
}

// واجهة مجموعة الرسوم
interface FeeGroup {
  id: string;
  name: string;
  description?: string;
  fees: Fee[];
}

// واجهة السند المتخصص
interface SpecializedVoucher {
  id?: string;
  templateId: string | {
    id: string;
    name?: string;
    voucherType?: string;
    requiresApproval?: boolean;
    hasFees?: boolean;
    [key: string]: any;
  };
  voucherNumber?: string;  // يتم إنشاؤه تلقائيًا في الخلفية
  voucherDate: string;
  debitAccountId: string;
  creditAccountId: string;
  amount: number;
  operationNumber?: string;
  notes?: string;
  appliedFeeGroupId?: string;
  calculatedFees?: FeeCalculation;
  isApproved?: boolean;
  branch?: string;  // إضافة معرف الفرع كحقل اختياري
}

// واجهة استجابة حساب الرسوم من الواجهة الخلفية
interface FeeCalculationResponse {
  feeGroupId: string;
  feeGroupName: string;
  amount: number;
  fixedFees: number;
  percentageFees: number;
  totalFees: number;
  feeBreakdown: {
    name: string;
    amount: number;
    isPercentage: boolean;
    rate: number | null;
  }[];
}

// واجهة حساب الرسوم للواجهة الأمامية
interface FeeCalculation {
  feeGroupId: string;
  feeGroupName: string;
  feeItems: {
    feeId: string;
    feeName: string;
    feeType: 'fixed' | 'percentage' | 'tax';
    amount: number;
  }[];
  totalFeesAmount: number;
  // حقول إضافية من واجهة API
  amount?: number;
  fixedFees?: number;
  percentageFees?: number;
  totalFees?: number;
  feeBreakdown?: {
    name: string;
    amount: number;
    isPercentage: boolean;
    rate: number | null;
  }[];
}

// Helper function to safely extract ID from various formats
const extractId = (value: any): string => {
  if (value === null || value === undefined) {
    console.warn('Null or undefined ID value encountered');
    return '';
  }
  
  if (typeof value === 'string') {
    return value;
  }
  
  if (typeof value === 'object') {
    // Handle case where value is an object with ID property
    if (value.id !== undefined) {
      return String(value.id);
    }
    
    // Handle case where value is an ObjectId or similar
    if (value._id !== undefined) {
      return String(value._id);
    }
    
    // Last resort - stringify the object
    console.warn('Complex object ID encountered, stringifying:', value);
    return String(value);
  }
  
  // For any other type, convert to string
  return String(value);
};

const SpecializedVoucherForm: React.FC = () => {
  const navigate = useNavigate();
  const { templateId: urlTemplateId, id } = useParams<{ templateId: string; id: string }>();
  const theme = useTheme();
  const { user, activeBranch } = useAuth();
  const location = window.location;
  
  // التحقق من كون المستخدم مدير نظام
  const isSystemAdmin = user?.isSystemAdmin || user?.role === 'admin';
  
  // استخراج مسار العودة من URL إذا كان موجوداً
  const returnPath = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const returnPathParam = urlParams.get('returnPath');
    return returnPathParam || '/finance/specialized-vouchers?tab=vouchers';
  }, []);
  
  // تسجيل معرف القالب مرة واحدة فقط عند تحميل المكون
  useEffect(() => {
    if (urlTemplateId) {
      console.log('قالب السند من عنوان URL:', urlTemplateId);
    }
  }, [urlTemplateId]);
  
  // Check if URL contains "view" to determine if we're in view-only mode
  const isViewMode = location.pathname.includes('/view/');
  const isEditMode = Boolean(id) && !isViewMode;
  
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [template, setTemplate] = useState<SpecializedVoucherTemplate | null>(null);
  const [debitAccounts, setDebitAccounts] = useState<Account[]>([]);
  const [creditAccounts, setCreditAccounts] = useState<Account[]>([]);
  const [feeGroups, setFeeGroups] = useState<FeeGroup[]>([]);
  const [feeCalculation, setFeeCalculation] = useState<FeeCalculation | null>(null);
  const [calculating, setCalculating] = useState<boolean>(false);
  
  const [availableTemplates, setAvailableTemplates] = useState<SpecializedVoucherTemplate[]>([]);
  
  // بيانات السند الحالي لتغيير الحالة
  const [currentVoucher, setCurrentVoucher] = useState<SpecializedVoucher | null>(null);
  const [canChangeVoucherStatus, setCanChangeVoucherStatus] = useState(false);
  const [statusChangeDialogOpen, setStatusChangeDialogOpen] = useState(false);
  
  // التحقق من صلاحيات السندات المتخصصة
  const { hasSpecializedVoucherPermission } = useSpecializedVoucherPermissions();
  
  // جلب قوالب السندات المتاحة
  useEffect(() => {
    const fetchAvailableTemplates = async () => {
      try {
        const { apiWithContext } = await import('../../../services/api');
        const templates = await apiWithContext.get('/finance/specialized-vouchers/templates', 'specialized-voucher').then(res => res.data);
        setAvailableTemplates(templates);
      } catch (error: any) {
        console.error('خطأ في جلب قوالب السندات:', error);
      }
    };
    
    fetchAvailableTemplates();
  }, []);
  
  // جلب بيانات القالب والحسابات ومجموعات الرسوم
  useEffect(() => {
    const fetchTemplateData = async () => {
      setLoading(true);
      console.log('بدء جلب بيانات القالب. معرف القالب من URL:', urlTemplateId);
      
      // فحص إذا كان هناك معرف قالب في التخزين المؤقت
      const cachedTemplateId = localStorage.getItem('lastSelectedTemplateId');
      const effectiveTemplateId = urlTemplateId || cachedTemplateId;
      
      console.log('معرف القالب الفعال:', effectiveTemplateId,
        'من URL:', urlTemplateId,
        'من التخزين المؤقت:', cachedTemplateId);
      
      setError(null);
      
      // لا نعرض واجهة اختيار القالب إذا كان هناك معرّف سند (id) لفتح سند محدد،
      // لأننا نستطيع استنتاج القالب من السند نفسه دون الاعتماد على التخزين المحلي
      if (!effectiveTemplateId && !id) {
        console.log('لم يتم توفير معرف قالب، عرض واجهة اختيار القالب');
        setLoading(false);
        return;
      }
      
      try {
        // جلب بيانات القالب
        let templateData: SpecializedVoucherTemplate;
        
        if (id) {
          // عند فتح سند محدد (عرض/تعديل) نجلب بيانات السند الموجود أولاً لاستنتاج معرف القالب منه
          const voucherResponse = await apiService.get<SpecializedVoucher>(`/finance/specialized-vouchers/${id}`);
          
          // تأكد من أن معرف القالب هو نص وليس كائن
          console.log('Template ID from voucher:', voucherResponse.templateId);
          const templateIdString = typeof voucherResponse.templateId === 'object' && voucherResponse.templateId !== null
            ? voucherResponse.templateId.id || String(voucherResponse.templateId)
            : String(voucherResponse.templateId);
          
          console.log('Using template ID string:', templateIdString);
          
          // تحقق من وجود معرف قالب
          if (!templateIdString) {
            throw new Error('لم يتم العثور على معرف قالب صالح في السند');
          }
          
          // ثم نجلب بيانات القالب المرتبط به
          console.log('جلب بيانات القالب للسند المعدل:', templateIdString);
          templateData = await apiService.get<SpecializedVoucherTemplate>(`/finance/specialized-vouchers/templates/${templateIdString}`);
          console.log('تم جلب بيانات القالب بنجاح:', templateData);
          
          // تعيين قيم النموذج مع معالجة معرف القالب بشكل صحيح
          const templateIdValue = typeof voucherResponse.templateId === 'object' && voucherResponse.templateId !== null
            ? voucherResponse.templateId.id || String(voucherResponse.templateId)
            : voucherResponse.templateId;
          
          // تأخير تعيين قيم النموذج حتى يتم تحميل الحسابات بنجاح
          console.log('تجهيز بيانات النموذج للتعديل. الحسابات:', {
            debit: voucherResponse.debitAccountId,
            credit: voucherResponse.creditAccountId
          });
          
          // تخزين معرف السند الذي يتم تعديله في حالة التحميل
          console.log('تخزين بيانات السند في المتغيرات المؤقتة للاستخدام لاحقًا');
          window.localStorage.setItem('editingVoucherId', id);
          window.localStorage.setItem('editingVoucherData', JSON.stringify({
            debitAccountId: voucherResponse.debitAccountId,
            creditAccountId: voucherResponse.creditAccountId,
            amount: voucherResponse.amount,
            operationNumber: voucherResponse.operationNumber || '',
            notes: voucherResponse.notes || '',
            appliedFeeGroupId: voucherResponse.appliedFeeGroupId || ''
          }));
          
          // تعيين حساب الرسوم إن وجد
          if (voucherResponse.calculatedFees) {
            setFeeCalculation(voucherResponse.calculatedFees);
          }
        } else if (urlTemplateId) {
          // في حالة الإنشاء، نجلب بيانات القالب المحدد
          console.log('جلب بيانات القالب في وضع الإنشاء، معرف القالب:', urlTemplateId);
          const { apiWithContext } = await import('../../../services/api');
          templateData = await apiWithContext.get(`/finance/specialized-vouchers/templates/${urlTemplateId}`, 'specialized-voucher').then(res => res.data);
          console.log('تم جلب بيانات القالب بنجاح:', templateData);
          
          // تعيين قيمة القالب في النموذج
          formik.setFieldValue('templateId', templateData.id);
          console.log('تم تعيين معرف القالب في النموذج:', templateData.id);
          
          // تعيين تاريخ اليوم كتاريخ افتراضي للسند
          formik.setFieldValue('voucherDate', new Date().toISOString().split('T')[0]);
        } else {
          throw new Error('لم يتم تحديد قالب السند');
        }
        // تعيين بيانات القالب
        setTemplate(templateData);
        
        // تخزين معرف القالب في التخزين المؤقت للاستخدام المستقبلي
        if (templateData && templateData.id) {
          console.log('حفظ معرف القالب في التخزين المؤقت:', templateData.id);
          localStorage.setItem('lastSelectedTemplateId', templateData.id);
        }
        
        
        // جلب بيانات الحسابات
        console.log('جلب بيانات الحسابات للقالب:', templateData.id);
        
        let debitAccountsData: Account[] = [];
        let creditAccountsData: Account[] = [];
        let feeGroupsData: FeeGroup[] = [];
        
        if (templateData.debitAccounts.length > 0) {
          try {
            console.log('جلب الحسابات المدينة:', templateData.debitAccounts);
            const { apiWithContext } = await import('../../../services/api');
            const debitAccountsResponse = await apiWithContext.post('/finance/accounts/filter', {
              ids: templateData.debitAccounts
            }, 'specialized-voucher').then(res => res.data);
            debitAccountsData = debitAccountsResponse;
            setDebitAccounts(debitAccountsResponse);
            console.log('تم جلب الحسابات المدينة بنجاح:', debitAccountsResponse.length);
          } catch (error) {
            console.error('خطأ في جلب الحسابات المدينة:', error);
          }
        }
        
        if (templateData.creditAccounts.length > 0) {
          try {
            console.log('جلب الحسابات الدائنة:', templateData.creditAccounts);
            const { apiWithContext } = await import('../../../services/api');
            const creditAccountsResponse = await apiWithContext.post('/finance/accounts/filter', {
              ids: templateData.creditAccounts
            }, 'specialized-voucher').then(res => res.data);
            creditAccountsData = creditAccountsResponse;
            setCreditAccounts(creditAccountsResponse);
            console.log('تم جلب الحسابات الدائنة بنجاح:', creditAccountsResponse.length);
          } catch (error) {
            console.error('خطأ في جلب الحسابات الدائنة:', error);
          }
        }
        
        // جلب بيانات مجموعات الرسوم إذا كان القالب يدعم الرسوم
        if (templateData.hasFees) {
          try {
            console.log('جلب مجموعات الرسوم - معرفات القالب:', templateData.feeGroups);
            // التحقق من وجود معرفات مجموعات رسوم في القالب
            if (!templateData.feeGroups || !Array.isArray(templateData.feeGroups) || templateData.feeGroups.length === 0) {
              console.warn('لا توجد معرفات مجموعات رسوم محددة في القالب');
              setFeeGroups([]);
            } else {
              console.log('جلب مجموعات الرسوم المسموح بها فقط للقالب:', templateData.feeGroups);
              
              // تأكد من أن جميع المعرفات صالحة
              const validIds = templateData.feeGroups.filter(id => id && id.toString().trim() !== '');
              
              if (validIds.length === 0) {
                console.warn('لا توجد معرفات صالحة لمجموعات الرسوم');
                setFeeGroups([]);
              } else {
                // نستخدم post/filter لضمان جلب مجموعات الرسوم المسموح بها فقط في هذا القالب
                const feeGroupsResponse = await apiService.post<FeeGroup[]>('/finance/fee-groups/filter', {
                  ids: validIds
                });
                
                console.log('تم جلب مجموعات الرسوم المسموح بها:', feeGroupsResponse.length);
                
                if (feeGroupsResponse && feeGroupsResponse.length > 0) {
                  feeGroupsData = feeGroupsResponse;
                  setFeeGroups(feeGroupsResponse);
                  console.log('تم تعيين مجموعات الرسوم المسموح بها:', feeGroupsResponse.length);
                } else {
                  console.warn('لا توجد مجموعات رسوم مسموحة في هذا القالب');
                  setFeeGroups([]);
                }
              }
            }
          } catch (error) {
            console.error('خطأ في جلب مجموعات الرسوم:', error);
            setFeeGroups([]);
          }
        } else {
          // إذا كان القالب لا يدعم الرسوم، تأكد من تفريغ مصفوفة مجموعات الرسوم
          setFeeGroups([]);
        }
        
        // في حالة التعديل، نعين قيم النموذج بعد جلب جميع البيانات
        if (isEditMode && id) {
          try {
            const voucherResponse = await apiService.get<SpecializedVoucher>(`/finance/specialized-vouchers/${id}`);
            
            // تعيين قيم النموذج
            const templateIdValue = typeof voucherResponse.templateId === 'object' && voucherResponse.templateId !== null
              ? voucherResponse.templateId.id || String(voucherResponse.templateId)
              : voucherResponse.templateId;
            
            // التحقق من وجود الحسابات قبل تعيين قيم النموذج
            console.log('تعيين قيم النموذج. الحسابات المتاحة:', {
              debit: debitAccountsData.map(a => a.id),
              credit: creditAccountsData.map(a => a.id),
              selectedDebit: voucherResponse.debitAccountId,
              selectedCredit: voucherResponse.creditAccountId
            });
            
            // استخراج المعرفات كنصوص لضمان توافقها مع التوقعات
            const debitIdString = extractId(voucherResponse.debitAccountId);
            const creditIdString = extractId(voucherResponse.creditAccountId);
            const feeGroupIdString = voucherResponse.appliedFeeGroupId ? extractId(voucherResponse.appliedFeeGroupId) : '';

            console.log('معرفات الحسابات بعد المعالجة:', {
              original: {
                debit: voucherResponse.debitAccountId,
                credit: voucherResponse.creditAccountId
              },
              extracted: {
                debit: debitIdString,
                credit: creditIdString
              }
            });
            
            formik.setValues({
              templateId: templateIdValue,
              voucherDate: voucherResponse.voucherDate ? new Date(voucherResponse.voucherDate).toISOString().split('T')[0] : '',
              debitAccountId: debitIdString,
              creditAccountId: creditIdString,
              amount: voucherResponse.amount,
              operationNumber: voucherResponse.operationNumber || '',
              notes: voucherResponse.notes || '',
              appliedFeeGroupId: feeGroupIdString
            });
            
            // تعيين حساب الرسوم إن وجد
            if (voucherResponse.calculatedFees) {
              setFeeCalculation(voucherResponse.calculatedFees);
            }
          } catch (error) {
            console.error('خطأ في تعيين قيم النموذج في وضع التعديل:', error);
          }
        }
        
        setLoading(false);
      } catch (error: any) {
        console.error('خطأ في جلب بيانات السند:', error);
        setError(error.response?.data?.message || 'حدث خطأ في جلب بيانات السند');
        setLoading(false);
      }
    };
    
    fetchTemplateData();
  }, [id, urlTemplateId, isEditMode]);
  
  // جلب بيانات السند والتحقق من صلاحيات تغيير الحالة في وضع العرض/التعديل
  useEffect(() => {
    const fetchVoucherAndCheckPermissions = async () => {
      if (!id || !isEditMode) return;
      
      try {
        const voucherResponse = await apiService.get<SpecializedVoucher>(`/finance/specialized-vouchers/${id}`);
        setCurrentVoucher(voucherResponse);
        
        // التحقق من صلاحية تغيير الحالة
        const templateId = typeof voucherResponse.templateId === 'object' && voucherResponse.templateId !== null
          ? voucherResponse.templateId.id
          : voucherResponse.templateId;
        setCanChangeVoucherStatus(hasSpecializedVoucherPermission(templateId, 'change_status'));
      } catch (error) {
        console.error('خطأ في جلب بيانات السند للتحقق من الصلاحيات:', error);
      }
    };
    
    fetchVoucherAndCheckPermissions();
  }, [id, isEditMode]);
  
  // مخطط التحقق من صحة النموذج
  const validationSchema = Yup.object({
    voucherDate: Yup.date().required('تاريخ السند مطلوب'),
    debitAccountId: Yup.string().required('الحساب المدين مطلوب'),
    creditAccountId: Yup.string().required('الحساب الدائن مطلوب'),
    amount: Yup.number()
      .required('المبلغ مطلوب')
      .min(0.01, 'يجب أن يكون المبلغ أكبر من صفر'),
    appliedFeeGroupId: Yup.string()
      .test('feeGroup-required', 'مجموعة الرسوم مطلوبة عند تفعيل الرسوم', function() {
        return !template?.hasFees || Boolean(this.parent.appliedFeeGroupId);
      })
  });
  
  // إعداد نموذج إنشاء/تعديل السند
  const formik = useFormik({
    initialValues: {
      templateId: urlTemplateId || '',
      voucherDate: new Date().toISOString().split('T')[0],
      debitAccountId: '',
      creditAccountId: '',
      amount: 0,
      operationNumber: '',
      notes: '',
      appliedFeeGroupId: ''
    },
    enableReinitialize: false, // منع إعادة التهيئة عند تغيير القيم الأولية
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      setError(null);
      
      try {
        // إزالة أي رسائل خطأ سابقة
        setError(null);
        
        // تأكد من أن جميع الحقول المطلوبة موجودة وصالحة
        console.log('قيم النموذج قبل الإرسال:', values);
        
        // التحقق من وجود قالب مختار
        if (!template || !template.id) {
          // في حالة عدم وجود قالب، نتحقق من وجود معرف في URL أو في قيم النموذج
          const urlOrFormTemplateId = urlTemplateId || values.templateId;
          
          if (urlOrFormTemplateId) {
            console.log('استخدام معرف القالب من URL أو النموذج:', urlOrFormTemplateId);
            values.templateId = urlOrFormTemplateId;
          } else {
            console.error('لم يتم اختيار قالب للسند بشكل صحيح:', { values, template });
            setError('يجب اختيار قالب السند');
            setLoading(false);
            return;
          }
        }
        
        // التأكد من تطابق معرف القالب في النموذج مع القالب المختار إذا كان موجودًا
        if (template && template.id) {
          if (!values.templateId || values.templateId === '' || values.templateId !== template.id) {
            console.error('عدم تطابق معرف القالب:', { formTemplateId: values.templateId, actualTemplateId: template.id });
            // تصحيح معرف القالب في النموذج
            values.templateId = template.id;
          }
        }
        
        if (!values.debitAccountId || values.debitAccountId === '') {
          setError('يجب اختيار الحساب المدين');
          setLoading(false);
          return;
        }
        
        if (!values.creditAccountId || values.creditAccountId === '') {
          setError('يجب اختيار الحساب الدائن');
          setLoading(false);
          return;
        }
        
        if (!values.amount || isNaN(Number(values.amount)) || Number(values.amount) <= 0) {
          setError('يجب إدخال مبلغ صحيح أكبر من صفر');
          setLoading(false);
          return;
        }
        
        // معالجة التاريخ: إضافة الوقت الحالي للتاريخ المحدد
        const branchTimezone = activeBranch?.timezone || 'Asia/Riyadh';
        let dateToSend: string;
        
        if (values.voucherDate) {
          // تحليل التاريخ من النموذج
          const dateStr = String(values.voucherDate);
          const [year, month, day] = dateStr.split('T')[0].split('-').map(Number);
          
          // الحصول على الوقت الحالي في المنطقة الزمنية للفرع
          const now = moment.tz(branchTimezone);
          
          // إنشاء التاريخ مع الوقت الحالي في المنطقة الزمنية للفرع
          const dateWithTime = moment.tz({
            year,
            month: month - 1,
            day,
            hour: now.hour(),
            minute: now.minute(),
            second: now.second()
          }, branchTimezone);
          
          dateToSend = dateWithTime.format('YYYY-MM-DDTHH:mm:ss.SSSZ');
        } else {
          dateToSend = moment.tz(branchTimezone).format('YYYY-MM-DDTHH:mm:ss.SSSZ');
        }
        
        const payload: SpecializedVoucher = {
          ...values,
          voucherDate: dateToSend,
          amount: Number(values.amount),
          calculatedFees: feeCalculation || undefined,
          // إضافة معرف الفرع إذا كان متاحًا
          branch: localStorage.getItem('activeBranchId') || undefined
        };
        
        console.log('البيانات المرسلة للخادم:', payload);
        
        // إضافة معرف القالب المختار مباشرة من المتغير template
        if (template && template.id) {
          console.log('استخدام معرف القالب من المتغير template:', template.id);
          payload.templateId = template.id;
        }
        
        // تأكد من أن معرف القالب موجود ومنسق بشكل صحيح
        const templateIdToSend = String(template?.id || payload.templateId);
        console.log('معرف القالب النهائي للإرسال:', templateIdToSend);
        
        // التحقق من صحة مجموعة الرسوم وتنظيف البيانات
        if (template?.hasFees && payload.appliedFeeGroupId) {
          // تأكد من أن مجموعة الرسوم المختارة مسموح بها في القالب
          const isAllowedFeeGroup = feeGroups.some(group => {
            const groupId = String(group.id || (group as any)._id || '');
            const payloadGroupId = String(payload.appliedFeeGroupId || '');
            return groupId === payloadGroupId;
          });
          
          if (!isAllowedFeeGroup) {
            setError('مجموعة الرسوم غير مسموحة في هذا القالب');
            setLoading(false);
            return;
          }
        }
          
        // إنشاء نسخة جديدة من الكائن مع معرف القالب المعالج
        const finalPayload = {
          ...payload,
          templateId: templateIdToSend
        };
        
        console.log('البيانات النهائية المرسلة للخادم:', finalPayload);
        
        // إعداد خيارات الطلب بإضافة رأس x-branch-id للتأكد من استخدام الفرع الصحيح
        const activeBranchId = localStorage.getItem('activeBranchId');
        const config = {
          headers: {
            'x-branch-id': activeBranchId || ''
          }
        };
        
        console.log('إرسال الطلب مع رأس الفرع:', config);
        
        if (isEditMode && id) {
          // تحديث سند موجود
          try {
            const response = await apiService.put(`/finance/specialized-vouchers/${id}`, finalPayload, config);
            console.log('تم تحديث السند بنجاح:', response);
            // العودة إلى مسار العودة المحدد بعد نجاح العملية
            navigate(returnPath);
          } catch (error: any) {
            console.error('خطأ في تحديث السند:', error);
            // استخراج رسالة الخطأ من الاستجابة بشكل أكثر تفصيلاً
            const errorMessage = error.response?.data?.message || 'حدث خطأ في تحديث السند';
            setError(errorMessage);
            setLoading(false);
          }
        } else {
          // إنشاء سند جديد
          try {
            const response = await apiService.post('/finance/specialized-vouchers', finalPayload, config);
            console.log('تم إنشاء السند بنجاح:', response);
            // العودة إلى مسار العودة المحدد بعد نجاح العملية
            navigate(returnPath);
          } catch (error: any) {
            console.error('خطأ في إنشاء السند:', error);
            // استخراج رسالة الخطأ من الاستجابة بشكل أكثر تفصيلاً
            const errorMessage = error.response?.data?.message || 'حدث خطأ في إنشاء السند';
            setError(errorMessage);
            setLoading(false);
          }
        }
      } catch (error: any) {
        console.error('خطأ في معالجة بيانات السند:', error);
        setError('حدث خطأ في معالجة بيانات السند: ' + error.message);
        setLoading(false);
      }
    }
  });
  
  // حساب الرسوم عند اختيار مجموعة رسوم أو تغيير المبلغ
  const calculateFees = async () => {
    // الحصول على أحدث القيم من النموذج
    const amount = formik.values.amount;
    
    // استخدام معرف المجموعة المحدد مؤخراً دائماً - هذا الخيار الأكثر موثوقية
    let appliedFeeGroupId = (window as any).currentFeeGroupId || formik.values.appliedFeeGroupId;
    
    console.log("🧮 بدء حساب الرسوم:", {
      معرف_المجموعة: appliedFeeGroupId,
      قيمة_النموذج: formik.values.appliedFeeGroupId,
      القيمة_العالمية: (window as any).selectedFeeGroupId,
      القيمة_الحالية: (window as any).currentFeeGroupId,
      المبلغ: amount
    });
    
    if (!template?.hasFees) {
      setFeeCalculation(null);
      return;
    }
    
    // فحص وجود معرف مجموعة صالح
    if (!appliedFeeGroupId || appliedFeeGroupId === '' || appliedFeeGroupId === 'undefined') {
      console.log("⛔ خطأ: لم يتم تحديد مجموعة رسوم");
      setError('يجب تحديد مجموعة الرسوم لحساب الرسوم');
      setTimeout(() => setError(null), 5000);
      setFeeCalculation(null);
      return;
    }
    
    if (amount <= 0) {
      console.log("⛔ خطأ: المبلغ غير صالح:", amount);
      setError('يجب إدخال مبلغ صحيح أكبر من صفر لحساب الرسوم');
      setTimeout(() => setError(null), 5000);
      setFeeCalculation(null);
      return;
    }
    
    console.log("🚀 جاري بدء حساب الرسوم...");
    setCalculating(true);
    
    try {
      console.log('معرف مجموعة الرسوم المرسل للحساب:', appliedFeeGroupId);
      console.log('نوع معرف مجموعة الرسوم:', typeof appliedFeeGroupId);
      console.log('جميع مجموعات الرسوم المتاحة:', feeGroups.map(g => ({ id: g.id, name: g.name })));
      
      let feeGroupIdToUse = appliedFeeGroupId;
      let groupName = '';
      
      // التحقق أولاً من أن مجموعة الرسوم مسموح بها في القالب الحالي
      if (!template || !template.hasFees) {
        throw new Error('القالب الحالي لا يدعم الرسوم');
      }
      
      console.log("🔍 البحث عن مجموعة الرسوم بالمعرف:", appliedFeeGroupId);
      
      // بحث دقيق بالمعرف فقط
      const selectedGroup = feeGroups.find(group => {
        const groupId = String(group.id || (group as any)._id);
        const searchId = String(appliedFeeGroupId);
        return groupId === searchId;
      });
      
      if (!selectedGroup) {
        console.warn('⛔ خطأ: مجموعة الرسوم غير موجودة بالمعرف:', appliedFeeGroupId);
        
        // عرض جميع مجموعات الرسوم المتاحة للتشخيص
        console.log("📋 مجموعات الرسوم المتاحة:", feeGroups.map(g => ({
          معرف: String(g.id || (g as any)._id),
          اسم: g.name
        })));
        
        throw new Error('مجموعة الرسوم المختارة غير موجودة. الرجاء تحديد مجموعة رسوم صالحة من القائمة');
      }
      
      // استخدام المعرف من المجموعة المختارة مباشرة دون تغيير
      const selectedGroupId = String(selectedGroup.id || (selectedGroup as any)._id);
      
      // تأكيد على استخدام المجموعة الصحيحة
      console.log('✅ تأكيد استخدام المجموعة الصحيحة:', {
        المعرف_المدخل: appliedFeeGroupId,
        المعرف_المؤكد: selectedGroupId,
        الاسم: selectedGroup.name
      });
      console.log('✅ تم العثور على مجموعة الرسوم:', {
        اسم: selectedGroup.name,
        معرف: selectedGroupId
      });
      
      // استخدام نفس المعرف المختار دون تغيير
      feeGroupIdToUse = selectedGroupId;
      groupName = selectedGroup.name;
      
      // تحديث المعرف العالمي للتأكد من استخدامه في كل مكان
      (window as any).selectedFeeGroupId = selectedGroupId;
      
      // إنشاء عنوان URL المناسب للواجهة الخلفية
      const url = '/finance/fee-groups/calculate';
      
      // تحضير بيانات الطلب
      // التحقق من صحة المعرف قبل تحضير الطلب - لا حاجة لإعادة الفحص هنا
      // حيث أننا تأكدنا بالفعل من وجود مجموعة رسوم صالحة في الخطوة السابقة
      
      // نستخدم المجموعة التي تم العثور عليها بالفعل في الخطوة السابقة (selectedGroup)
      // وتأكدنا من وجودها بالفعل، لذا لا حاجة للبحث مرة أخرى
      const selectedFeeGroup = selectedGroup;
      
      // لقد تحققنا بالفعل من وجود مجموعة الرسوم في الخطوة السابقة
      // لذا لا داعي للتحقق مرة أخرى هنا
      
      // استخدام معرف المجموعة المؤكد
      // استخراج المعرف بطريقة آمنة سواء كان id أو _id
      const feeGroupId = selectedFeeGroup.id || (selectedFeeGroup as any)._id;
      
      // تحديث المتغيرات العالمية للتأكد من استخدام نفس المعرف في كل مكان
      (window as any).selectedFeeGroupId = selectedGroupId;
      (window as any).selectedFeeGroupName = selectedGroup.name;
      (window as any).currentFeeGroupId = selectedGroupId;
      
      // إعداد بيانات الطلب مع التأكد من استخدام نفس المعرف
      const payload = {
        feeGroupId: selectedGroupId,
        amount: parseFloat(amount.toString())
      };
      
      console.log('📤 إرسال طلب حساب الرسوم:', {
        عنوان: url,
        بيانات: payload
      });
      
      // إرسال طلب حساب الرسوم
      const backendResponse = await apiService.post<FeeCalculationResponse>(url, payload);
      
      console.log('📥 استجابة حساب الرسوم:', backendResponse);
      
      // التأكد من استخدام نفس معرف المجموعة المختارة للاستعلام عن البيانات
      try {
        console.log(`جلب تفاصيل مجموعة الرسوم بالمعرف [${feeGroupId}]`);
        const { apiWithContext } = await import('../../../services/api');
        const feeGroupDetails = await apiWithContext.get(`/finance/fee-groups/${feeGroupId}`, 'specialized-voucher').then(res => res.data);
        console.log('بيانات مجموعة الرسوم من قاعدة البيانات:', feeGroupDetails);
        
        // استخراج قيم الرسوم مباشرة من الكائن المسترجع من قاعدة البيانات
        const originalFees = feeGroupDetails.fees || [];
        console.log(`الرسوم الأصلية من مجموعة "${feeGroupDetails.name}"`, originalFees);
        
        // التأكد أولاً من أن الرسوم نشطة وتنتمي فعلاً للمجموعة المختارة
        const activeFees = originalFees.filter(fee => fee.isActive === true);
        
        // تسجيل الرسوم النشطة للتأكد من صحة البيانات
        console.log('الرسوم النشطة في المجموعة المختارة:', activeFees.map(f => f.name));
        
        // تصنيف الرسوم إلى فئات بناءً على معايير واضحة ومحددة
        // 1. رسوم ضريبية - بناءً على نوع الرسم أو الوصف
        const taxFees = activeFees.filter(fee =>
          fee.type === 'tax' ||
          (fee.description && (
            fee.description.includes('ضريبة') ||
            fee.description.includes('VAT')
          )) ||
          (fee.name && (
            fee.name.includes('ضريبة') ||
            fee.name.includes('VAT')
          )) ||
          (fee.percentage === true && fee.amount === 15)
        );
        
        // 2. الرسوم غير الضريبية (استبعاد ما تم تصنيفه كضريبة)
        const nonTaxFees = activeFees.filter(fee =>
          !taxFees.includes(fee)
        );
        
        // تقسيم الرسوم غير الضريبية بوضوح
        const fixedFees = nonTaxFees.filter(fee =>
          fee.type === 'fixed' ||
          fee.percentage === false ||
          (!fee.percentage && fee.amount > 0)
        );
        
        const percentageFees = nonTaxFees.filter(fee =>
          fee.type === 'percentage' ||
          fee.percentage === true
        );
        
        // منع الازدواجية في التصنيف - التأكد من عدم ظهور أي رسم في أكثر من فئة
        console.log('التأكد من عدم وجود ازدواجية في تصنيف الرسوم:', {
          'إجمالي_الرسوم_النشطة': activeFees.length,
          'مجموع_الرسوم_المصنفة': taxFees.length + fixedFees.length + percentageFees.length
        });
        
        console.log('تصنيف الرسوم الجديد:', {
          ثابتة: fixedFees.map(f => f.name),
          نسبية: percentageFees.map(f => f.name),
          ضريبية: taxFees.map(f => f.name)
        });
        
        // حساب إجمالي الرسوم الثابتة
        let totalFixedFees = 0;
        fixedFees.forEach(fee => {
          totalFixedFees += Number(fee.amount || 0);
        });
        
        // حساب إجمالي الرسوم النسبية
        let totalPercentageFees = 0;
        percentageFees.forEach(fee => {
          totalPercentageFees += (Number(formik.values.amount) * Number(fee.amount || 0) / 100);
        });
        
        // مجموع الرسوم الثابتة والنسبية (قبل الضريبة)
        const subtotal = totalFixedFees + totalPercentageFees;
        
        // حساب الضرائب على مجموع الرسوم السابقة
        let totalTaxFees = 0;
        
        // إنشاء مصفوفة لتخزين تفاصيل الضرائب لاستخدامها في التقارير
        const taxDetails = taxFees.map(fee => {
          // استخدام subtotal (مجموع الرسوم الثابتة والنسبية) كأساس للضريبة
          const taxBase = subtotal;
          const taxRate = Number(fee.amount || 0);
          
          // حساب الضريبة على مجموع الرسوم
          const taxAmount = taxBase * taxRate / 100;
          
          console.log(`حساب الضريبة: ${taxBase} ريال × ${taxRate}% = ${taxAmount} ريال`);
          console.log(`ملاحظة: تم تجاهل القيمة المحسوبة من الخادم (${fee.amount * formik.values.amount / 100} ريال)`);
          
          // إضافة قيمة الضريبة للمجموع
          totalTaxFees += taxAmount;
          
          // إرجاع معلومات مفصلة عن هذه الضريبة
          return {
            name: fee.name,
            rate: taxRate,
            base: taxBase,
            amount: taxAmount
          };
        });
        
        // إجمالي جميع الرسوم (بما فيها الضرائب) - تصحيح المجموع
        const totalAllFees = subtotal + totalTaxFees;
        
        // حساب القيم الفعلية المستخدمة في الواجهة
        const actualTaxFees = taxFees.reduce((sum, fee) => {
          const taxBase = subtotal;
          const taxRate = Number(fee.amount || 0);
          return sum + (taxBase * taxRate / 100);
        }, 0);
        
        const actualTotalFees = totalFixedFees + totalPercentageFees + actualTaxFees;
        
        // تقرير مفصل عن مكونات الرسوم مع معلومات تفصيلية لتسهيل التتبع
        console.log('تقرير مفصل عن مكونات الرسوم:', {
          مجموعة_الرسوم: {
            معرف: feeGroupIdToUse,
            اسم: feeGroupDetails.name
          },
          تصنيف_الرسوم: {
            ثابتة: fixedFees.map(f => ({ name: f.name, amount: f.amount })),
            نسبية: percentageFees.map(p => ({ name: p.name, rate: p.amount, amount: (Number(formik.values.amount) * Number(p.amount) / 100) })),
            ضريبية: taxDetails // الآن تم تعريف هذا المتغير بشكل صحيح
          },
          المبالغ_المحسوبة: {
            رسوم_ثابتة: totalFixedFees,
            رسوم_نسبية: totalPercentageFees,
            المجموع_قبل_الضريبة: subtotal,
            الضرائب: totalTaxFees,
            الإجمالي_النهائي: totalFixedFees + totalPercentageFees + totalTaxFees
          }
        });
        
        // إنشاء كائن الرسوم بناءً على البيانات الفعلية
        const feeCalculation: FeeCalculation = {
          // استخدام معرف مجموعة الرسوم التي تم اختيارها بالفعل
          feeGroupId: feeGroupId,
          feeGroupName: feeGroupDetails.name,
          // ضمان حساب الإجمالي بشكل صحيح دون ازدواجية
          totalFeesAmount: totalFixedFees + totalPercentageFees + totalTaxFees,
          // تحويل الرسوم الأصلية إلى التنسيق المطلوب
          feeItems: [
            // الرسوم الثابتة
            ...fixedFees.map(fee => ({
              feeId: fee._id || fee.id || String(Math.random()),
              feeName: fee.name,
              feeType: 'fixed',
              amount: Number(fee.amount || 0)
            })),
            // الرسوم النسبية
            ...percentageFees.map(fee => ({
              feeId: fee._id || fee.id || String(Math.random()),
              feeName: fee.name,
              feeType: 'percentage',
              amount: Number(formik.values.amount) * Number(fee.amount || 0) / 100
            })),
            // الضرائب - تأكيد استخدام subtotal كقاعدة للضريبة
            ...taxFees.map(fee => {
              const taxBase = subtotal; // مجموع الرسوم
              const taxRate = Number(fee.amount || 0);
              const taxAmount = taxBase * taxRate / 100;
              
              return {
                feeId: fee._id || fee.id || String(Math.random()),
                feeName: fee.name,
                feeType: 'tax',
                amount: taxAmount
              };
            })
          ]
        };
        
        console.log('بيانات الرسوم النهائية:', feeCalculation);
        console.log('إجمالي الرسوم النهائي:', feeCalculation.totalFeesAmount);
        
        // التحقق من قيمة المجموع ومطابقتها
        const calculatedTotal = feeCalculation.feeItems.reduce((sum, item) => sum + item.amount, 0);
        console.log('التحقق من المجموع النهائي:', {
          مجموع_العناصر_الفردية: calculatedTotal,
          قيمة_المجموع_المخزنة: feeCalculation.totalFeesAmount,
          متطابقة: calculatedTotal === feeCalculation.totalFeesAmount
        });
        
        // تصحيح قيمة totalFeesAmount لضمان مطابقتها للمجموع الفعلي للعناصر
        feeCalculation.totalFeesAmount = calculatedTotal;
        console.log('إجمالي الرسوم النهائي بعد التصحيح:', feeCalculation.totalFeesAmount.toFixed(2));
        
        setFeeCalculation(feeCalculation);
      } catch (error) {
        console.error('خطأ في جلب بيانات الرسوم من قاعدة البيانات:', error);
        
        // في حالة الفشل، نعود للطريقة الأصلية
        const feeCalculation: FeeCalculation = {
          feeGroupId: backendResponse.feeGroupId,
          feeGroupName: backendResponse.feeGroupName,
          totalFeesAmount: Number(backendResponse.totalFees || 0),
          feeItems: backendResponse.feeBreakdown ? backendResponse.feeBreakdown.map(item => ({
            feeId: item.name,
            feeName: item.name,
            feeType: item.isPercentage ? 'percentage' : 'fixed',
            amount: Number(item.amount || 0)
          })) : []
        };
        
        setFeeCalculation(feeCalculation);
      }
      setCalculating(false);
    } catch (error: any) {
      console.error('خطأ في حساب الرسوم:', error);
      setCalculating(false);
      
      const errorMessage = error.message || 'خطأ غير معروف أثناء حساب الرسوم';
      alert(errorMessage);
      setFeeCalculation(null);
    }
  };
  
  // عرض نوع السند بالعربية
  const getVoucherTypeText = (type: 'receipt' | 'payment' | 'journal') => {
    switch (type) {
      case 'receipt': return 'سند قبض';
      case 'payment': return 'سند صرف';
      case 'journal': return 'سند موازنة';
      default: return '';
    }
  };
  
  // حساب المبلغ النهائي بعد خصم الرسوم
  const finalAmount = useMemo(() => {
    if (!template?.hasFees || !feeCalculation) return formik.values.amount;
    
    // حساب إجمالي الرسوم من العناصر الفردية
    const totalActualFees = feeCalculation.feeItems.reduce((sum, item) => sum + item.amount, 0);
    
    // المبلغ النهائي = المبلغ الأصلي - إجمالي الرسوم الفعلي
    return Number(formik.values.amount) - totalActualFees;
  }, [formik.values.amount, feeCalculation, template?.hasFees]);
  
  // تغيير حالة السند (معتمد/قيد الانتظار)
  const handleToggleVoucherStatus = async () => {
    if (!currentVoucher || !id) return;
    
    try {
      setLoading(true);
      
      // إنشاء الطلب - تغيير الحالة إلى عكس الحالة الحالية
      const response = await apiService.put(`/finance/specialized-vouchers/${id}`, {
        isApproved: !currentVoucher.isApproved
      });
      
      // عرض رسالة نجاح
      setSuccessMessage(`تم تغيير حالة السند "${currentVoucher.voucherNumber}" بنجاح`);
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
      
      // تحديث بيانات السند الحالي
      setCurrentVoucher({
        ...currentVoucher,
        isApproved: !currentVoucher.isApproved
      });
      
      // إعادة تحميل بيانات السند
      if (isEditMode && id) {
        const updatedVoucher = await apiService.get<SpecializedVoucher>(`/finance/specialized-vouchers/${id}`);
        setCurrentVoucher(updatedVoucher);
      }
      
      // إغلاق حوار تغيير الحالة
      setStatusChangeDialogOpen(false);
    } catch (error: any) {
      console.error('خطأ في تغيير حالة السند:', error);
      setError(error.response?.data?.message || 'حدث خطأ في تغيير حالة السند');
      setTimeout(() => {
        setError(null);
      }, 5000);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={2} sx={{ p: 3 }}>
        {/* رأس الصفحة */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
              {isViewMode
                ? 'عرض تفاصيل السند المتخصص'
                : isEditMode
                  ? 'تعديل سند متخصص'
                  : 'إنشاء سند متخصص جديد'
              }
              {template && (
                <Typography component="span" variant="subtitle1" color="text.secondary" sx={{ mr: 1 }}>
                  ({template.name})
                </Typography>
              )}
            </Typography>
            
            {/* عرض حالة السند */}
            {currentVoucher && (
              <Chip
                label={currentVoucher.isApproved ? 'معتمد' : 'قيد الانتظار'}
                color={currentVoucher.isApproved ? 'success' : 'warning'}
                size="small"
                sx={{ fontWeight: 500 }}
              />
            )}
          </Box>
          
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(returnPath)}
          >
            العودة للقائمة
          </Button>
        </Box>
        
        {/* عرض الأخطاء */}
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => setError(null)}
          >
            <Typography variant="body1" component="div">
              {error}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              (يمكنك محاولة إعادة الإرسال أو التحقق من البيانات المدخلة)
            </Typography>
          </Alert>
        )}
        
        {/* حالة التحميل */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress size={40} sx={{ color: theme.palette.primary.main }} />
          </Box>
        ) : (
          <>
            {/* Show template selection if no template is loaded */}
            {!template && (
              <Card variant="outlined" sx={{ mb: 3, bgcolor: alpha(theme.palette.info.main, 0.05), p: 3 }}>
                <CardHeader
                  title="اختيار قالب السند"
                  titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
                  sx={{ pb: 0 }}
                />
                <CardContent>
                  <Alert severity="info" sx={{ mb: 3 }}>
                    يجب اختيار قالب السند أولاً قبل إنشاء السند المتخصص
                  </Alert>
                  
                  {availableTemplates.length > 0 ? (
                    <Grid container spacing={2}>
                      {availableTemplates.map((templateItem) => (
                        <Grid item xs={12} md={4} key={templateItem.id}>
                          <Card
                            variant="outlined"
                            sx={{
                              cursor: 'pointer',
                              transition: 'all 0.3s',
                              '&:hover': {
                                boxShadow: 3,
                                borderColor: theme.palette.primary.main
                              }
                            }}
                            onClick={async () => {
                              try {
                                setLoading(true);
                                console.log('اختيار القالب:', templateItem.name, 'معرف:', templateItem.id);
                                
                                // حفظ معرف القالب في التخزين المحلي والنموذج أولاً
                                localStorage.setItem('lastSelectedTemplateId', templateItem.id);
                                
                                // تعيين قيمة معرف القالب في النموذج
                                await formik.setFieldValue('templateId', templateItem.id, true);
                                console.log('تم تعيين معرف القالب في النموذج:', templateItem.id);
                                
                                // جلب بيانات القالب والحسابات ومجموعات الرسوم
                                const templateData = await apiService.get<SpecializedVoucherTemplate>(
                                  `/finance/specialized-vouchers/templates/${templateItem.id}`
                                );
                                
                                console.log('تم جلب بيانات القالب:', templateData);
                                
                                // تعيين بيانات القالب في متغير حالة
                                setTemplate(templateData);
                                
                                // جلب بيانات الحسابات
                                if (templateData.debitAccounts.length > 0) {
                                  const debitAccountsResponse = await apiService.post<Account[]>('/finance/accounts/filter', {
                                    ids: templateData.debitAccounts
                                  });
                                  setDebitAccounts(debitAccountsResponse);
                                }
                                
                                if (templateData.creditAccounts.length > 0) {
                                  const creditAccountsResponse = await apiService.post<Account[]>('/finance/accounts/filter', {
                                    ids: templateData.creditAccounts
                                  });
                                  setCreditAccounts(creditAccountsResponse);
                                }
                                
                                // جلب بيانات مجموعات الرسوم إذا كان القالب يدعم الرسوم
                                if (templateData.hasFees) {
                                  if (!templateData.feeGroups || templateData.feeGroups.length === 0) {
                                    console.warn('القالب يدعم الرسوم ولكن لا توجد مجموعات رسوم محددة به');
                                    setFeeGroups([]);
                                  } else {
                                    // تجنب المشاكل المحتملة من خلال التأكد من صحة المعرفات
                                    const validIds = templateData.feeGroups.filter(id => id && id.toString().trim() !== '');
                                    
                                    if (validIds.length > 0) {
                                      try {
                                        const { apiWithContext } = await import('../../../services/api');
                                        const feeGroupsResponse = await apiWithContext.post('/finance/fee-groups/filter', {
                                          ids: validIds
                                        }, 'specialized-voucher').then(res => res.data);
                                        
                                        // تأكد من أن كل مجموعة رسوم لها معرف صالح
                                        const validFeeGroups = feeGroupsResponse.filter(group => group && group.id);
                                        setFeeGroups(validFeeGroups);
                                        console.log('تم جلب مجموعات الرسوم بنجاح:', validFeeGroups.length);
                                        
                                        // تفريغ مجموعة الرسوم المختارة الحالية
                                        formik.setFieldValue('appliedFeeGroupId', '', false);
                                      } catch (error) {
                                        console.error('خطأ في جلب مجموعات الرسوم:', error);
                                        setFeeGroups([]);
                                      }
                                    } else {
                                      console.warn('لا توجد معرفات صالحة لمجموعات الرسوم');
                                      setFeeGroups([]);
                                    }
                                  }
                                } else {
                                  // إذا كان القالب لا يدعم الرسوم، تأكد من تفريغ قائمة مجموعات الرسوم
                                  setFeeGroups([]);
                                  formik.setFieldValue('appliedFeeGroupId', '', false);
                                }
                                
                                setError(null);
                                
                                // تأكيد من قيمة النموذج بعد التعيين
                                console.log('قيمة templateId في النموذج بعد التعيين:', formik.values.templateId);
                              } catch (error: any) {
                                console.error('خطأ في جلب بيانات القالب:', error);
                                setError('حدث خطأ أثناء جلب بيانات القالب');
                              } finally {
                                setLoading(false);
                              }
                            }}
                          >
                            <CardContent>
                              <Typography variant="h6" gutterBottom>
                                {templateItem.name}
                              </Typography>
                              <Chip
                                label={getVoucherTypeText(templateItem.voucherType)}
                                variant="outlined"
                                size="small"
                                color={
                                  templateItem.voucherType === 'receipt' ? 'success' :
                                  templateItem.voucherType === 'payment' ? 'error' : 'primary'
                                }
                                sx={{ mb: 1 }}
                              />
                              <Typography variant="body2" color="text.secondary">
                                {templateItem.description || 'لا يوجد وصف'}
                              </Typography>
                              <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {templateItem.requiresApproval && (
                                  <Chip label="يتطلب موافقة" size="small" />
                                )}
                                {templateItem.hasFees && (
                                  <Chip label="يحتوي على رسوم" size="small" />
                                )}
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Box sx={{ mb: 2 }}>
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        لم يتم العثور على قوالب سندات متاحة. يرجى إنشاء قوالب سندات أولاً.
                      </Alert>
                      
                      <Typography variant="body2" gutterBottom>
                        أو أدخل معرف قالب السند يدوياً:
                      </Typography>
                      
                      <TextField
                        fullWidth
                        id="templateIdInput"
                        label="معرف قالب السند"
                        value={formik.values.templateId}
                        onChange={formik.handleChange('templateId')}
                        error={Boolean(formik.errors.templateId)}
                        helperText={formik.errors.templateId}
                        required
                        sx={{ mb: 2 }}
                      />
                      
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={async () => {
                          const templateId = formik.values.templateId;
                          if (!templateId || templateId === '') {
                            setError('يجب إدخال معرف قالب السند');
                            return;
                          }
                          
                          try {
                            setLoading(true);
                            const templateData = await apiService.get<SpecializedVoucherTemplate>(
                              `/finance/specialized-vouchers/templates/${templateId}`
                            );
                            setTemplate(templateData);
                            localStorage.setItem('lastSelectedTemplateId', templateId);
                            setError(null);
                            setLoading(false);
                          } catch (error) {
                            console.error('خطأ في جلب بيانات القالب:', error);
                            setError('قالب السند غير موجود');
                            setLoading(false);
                          }
                        }}
                        disabled={!formik.values.templateId || loading}
                      >
                        تأكيد واختيار القالب
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}
            
            {/* Show regular form when template is loaded */}
            {template && (
              <>
              {/* معلومات القالب */}
              <Card variant="outlined" sx={{ mb: 3, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                <CardContent>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" color="text.secondary">
                        نوع السند
                      </Typography>
                      <Chip
                        label={getVoucherTypeText(template.voucherType)}
                        variant="outlined"
                        size="small"
                        color={
                          template.voucherType === 'receipt' ? 'success' :
                          template.voucherType === 'payment' ? 'error' : 'primary'
                        }
                        sx={{ mt: 0.5 }}
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" color="text.secondary">
                        يتطلب موافقة
                      </Typography>
                      <Typography variant="body2">
                        {template.requiresApproval ? 'نعم' : 'لا'}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" color="text.secondary">
                        تطبيق رسوم
                      </Typography>
                      <Typography variant="body2">
                        {template.hasFees ? 'نعم' : 'لا'}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
              
              {/* نموذج إنشاء/تعديل السند */}
              <form onSubmit={formik.handleSubmit}>
                <Grid container spacing={3}>
                  {/* القسم الأول: معلومات السند الأساسية */}
                  <Grid item xs={12}>
                    <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 1px 5px rgba(0,0,0,0.05)' }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          معلومات السند
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              id="voucherDate"
                              name="voucherDate"
                              label="تاريخ السند"
                              type="date"
                              value={formik.values.voucherDate}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              error={formik.touched.voucherDate && Boolean(formik.errors.voucherDate)}
                              helperText={formik.touched.voucherDate && formik.errors.voucherDate}
                              InputLabelProps={{
                                shrink: true,
                              }}
                              disabled={!isSystemAdmin}
                              required
                            />
                          </Grid>
                          
                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              id="operationNumber"
                              name="operationNumber"
                              label="رقم العملية"
                              value={formik.values.operationNumber}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              error={formik.touched.operationNumber && Boolean(formik.errors.operationNumber)}
                              helperText={formik.touched.operationNumber && formik.errors.operationNumber}
                            />
                          </Grid>
                          
                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              id="amount"
                              name="amount"
                              label="المبلغ"
                              type="number"
                              value={formik.values.amount}
                              onChange={(e) => {
                                formik.handleChange(e);
                                // إعادة تعيين حساب الرسوم عند تغيير المبلغ
                                setFeeCalculation(null);
                                
                                // حساب الرسوم تلقائيًا فقط إذا كان هناك مجموعة رسوم مختارة بالفعل والمبلغ أكبر من صفر
                                if (template?.hasFees && Number(e.target.value) > 0) {
                                  // تأخير طفيف للسماح للقيمة بالتحديث أولاً
                                  setTimeout(() => {
                                    // تحقق مرة أخرى من وجود مجموعة الرسوم قبل الحساب
                                    if (formik.values.appliedFeeGroupId) {
                                      calculateFees();
                                    }
                                  }, 100);
                                }
                              }}
                              onBlur={formik.handleBlur}
                              error={formik.touched.amount && Boolean(formik.errors.amount)}
                              helperText={formik.touched.amount && formik.errors.amount}
                              required
                            />
                          </Grid>
                          
                          <Grid item xs={12} md={6}>
                            <FormControl fullWidth required error={formik.touched.debitAccountId && Boolean(formik.errors.debitAccountId)}>
                              <InputLabel id="debit-account-label">من حساب (المدين)</InputLabel>
                              <Select
                                labelId="debit-account-label"
                                id="debitAccountId"
                                name="debitAccountId"
                                value={formik.values.debitAccountId || ''}
                                onChange={(e) => {
                                  formik.handleChange(e);
                                  console.log("تم اختيار الحساب المدين:", e.target.value);
                                }}
                                onBlur={formik.handleBlur}
                                label="من حساب (المدين)"
                                MenuProps={{
                                  PaperProps: {
                                    style: {
                                      maxHeight: 300
                                    }
                                  },
                                  anchorOrigin: {
                                    vertical: 'bottom',
                                    horizontal: 'right',
                                  },
                                  transformOrigin: {
                                    vertical: 'top',
                                    horizontal: 'right',
                                  }
                                }}
                              >
                                {debitAccounts.map(account => (
                                  <MenuItem
                                    key={account.id}
                                    value={account.id}
                                    style={{ textAlign: 'right', direction: 'rtl' }}
                                  >
                                    {account.code} - {account.name}
                                  </MenuItem>
                                ))}
                              </Select>
                              {formik.touched.debitAccountId && formik.errors.debitAccountId && (
                                <Typography color="error" variant="caption">
                                  {String(formik.errors.debitAccountId)}
                                </Typography>
                              )}
                            </FormControl>
                          </Grid>
                          
                          <Grid item xs={12} md={6}>
                            <FormControl fullWidth required error={formik.touched.creditAccountId && Boolean(formik.errors.creditAccountId)}>
                              <InputLabel id="credit-account-label">إلى حساب (الدائن)</InputLabel>
                              <Select
                                labelId="credit-account-label"
                                id="creditAccountId"
                                name="creditAccountId"
                                value={formik.values.creditAccountId}
                                onChange={(e) => {
                                  formik.setFieldValue("creditAccountId", e.target.value);
                                  console.log("تم اختيار الحساب الدائن:", e.target.value);
                                }}
                                onBlur={formik.handleBlur}
                                label="إلى حساب (الدائن)"
                                MenuProps={{
                                  PaperProps: {
                                    style: {
                                      maxHeight: 300
                                    }
                                  },
                                  anchorOrigin: {
                                    vertical: 'bottom',
                                    horizontal: 'right',
                                  },
                                  transformOrigin: {
                                    vertical: 'top',
                                    horizontal: 'right',
                                  }
                                }}
                              >
                                {creditAccounts.map(account => (
                                  <MenuItem
                                    key={account.id}
                                    value={account.id}
                                    style={{ textAlign: 'right', direction: 'rtl' }}
                                  >
                                    {account.code} - {account.name}
                                  </MenuItem>
                                ))}
                              </Select>
                              {formik.touched.creditAccountId && formik.errors.creditAccountId && (
                                <Typography color="error" variant="caption">
                                  {String(formik.errors.creditAccountId)}
                                </Typography>
                              )}
                            </FormControl>
                          </Grid>
                          
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              id="notes"
                              name="notes"
                              label="ملاحظات"
                              value={formik.values.notes}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              multiline
                              rows={3}
                            />
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  {/* القسم الثاني: مجموعات الرسوم (إذا كان القالب يدعم الرسوم) */}
                  {template.hasFees && (
                    <Grid item xs={12}>
                      <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 1px 5px rgba(0,0,0,0.05)' }}>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            الرسوم المطبقة
                          </Typography>
                          <Divider sx={{ mb: 2 }} />
                          
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              {/* استخدام نهج بسيط للقائمة المنسدلة */}
                              <FormControl fullWidth required error={formik.touched.appliedFeeGroupId && Boolean(formik.errors.appliedFeeGroupId)}>
                                <InputLabel id="fee-group-label">مجموعة الرسوم</InputLabel>
                                <Select
                                  labelId="fee-group-label"
                                  id="appliedFeeGroupId"
                                  name="appliedFeeGroupId"
                                  value={formik.values.appliedFeeGroupId || ''}
                                  onChange={(e) => {
                                    const newValue = e.target.value;
                                    console.log("🔄 تغيير مجموعة الرسوم إلى:", newValue);
                                    
                                    // مسح المتغير العالمي عند تغيير المجموعة
                                    (window as any).selectedFeeGroupId = null;
                                    (window as any).currentFeeGroupId = newValue;
                                    
                                    // تحديث قيمة النموذج بالمعرف المختار بشكل مباشر
                                    formik.setFieldValue("appliedFeeGroupId", newValue);
                                    
                                    // البحث عن المجموعة المختارة
                                    if (newValue && newValue !== '') {
                                      const selectedGroup = feeGroups.find(g =>
                                        String(g.id) === String(newValue) ||
                                        String((g as any)._id) === String(newValue)
                                      );
                                      
                                      if (selectedGroup) {
                                        const selectedId = String(selectedGroup.id || (selectedGroup as any)._id);
                                        console.log("✅ تم اختيار مجموعة صالحة:", {
                                          معرف: selectedId,
                                          اسم: selectedGroup.name
                                        });
                                        
                                        // تحديث المتغير العالمي
                                        (window as any).selectedFeeGroupId = selectedId;
                                        (window as any).selectedFeeGroupName = selectedGroup.name;
                                        
                                        // طباعة سجل مفصل
                                        console.log("📊 معلومات المجموعة المختارة:", {
                                          معرف: selectedId,
                                          اسم: selectedGroup.name,
                                          عدد_الرسوم: selectedGroup.fees?.length || 0
                                        });
                                      } else {
                                        console.warn("⚠️ المجموعة المختارة غير موجودة!", newValue);
                                        setError('مجموعة الرسوم المختارة غير متوفرة. الرجاء اختيار مجموعة أخرى.');
                                        setTimeout(() => setError(null), 3000);
                                        return;
                                      }
                                    } else {
                                      // إذا تم مسح الاختيار
                                      console.log("🗑️ تم مسح اختيار مجموعة الرسوم");
                                    }
                                    
                                    // إعادة تعيين حساب الرسوم
                                    setFeeCalculation(null);
                                    
                                    // حساب الرسوم تلقائيًا إذا كان المبلغ أكبر من صفر ومجموعة الرسوم صالحة
                                    if (newValue && newValue !== 'undefined' && newValue !== '') {
                                      // زيادة وقت الانتظار لضمان تحديث القيم أولاً
                                      setTimeout(() => {
                                        console.log("⏱️ التحقق من قيم النموذج قبل حساب الرسوم:", {
                                          معرف_المجموعة: formik.values.appliedFeeGroupId,
                                          المبلغ: formik.values.amount
                                        });
                                        
                                        if (formik.values.amount > 0) {
                                          calculateFees();
                                        }
                                      }, 200); // زيادة وقت الانتظار لضمان تحديث القيم
                                    }
                                  }}
                                  onBlur={formik.handleBlur}
                                  label="مجموعة الرسوم"
                                >
                                  <MenuItem value="">
                                    <em>اختر مجموعة الرسوم</em>
                                  </MenuItem>
                                  {feeGroups.map(group => {
                                    // استخدام معرف موحد
                                    const groupId = String(group.id || (group as any)._id || '');
                                    return (
                                      <MenuItem
                                        key={groupId}
                                        value={groupId}
                                      >
                                        {group.name}
                                      </MenuItem>
                                    );
                                  })}
                                </Select>
                                {formik.touched.appliedFeeGroupId && formik.errors.appliedFeeGroupId && (
                                  <Typography color="error" variant="caption">
                                    {String(formik.errors.appliedFeeGroupId)}
                                  </Typography>
                                )}
                              </FormControl>
                              
                              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                                {calculating && (
                                  <Box display="flex" alignItems="center">
                                    <CircularProgress size={20} sx={{ mr: 1 }} />
                                    <Typography variant="body2" color="text.secondary">
                                      جاري حساب الرسوم...
                                    </Typography>
                                  </Box>
                                )}
                                
                                <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                  {calculating
                                    ? 'جاري الحساب...'
                                    : feeCalculation
                                      ? 'تم حساب الرسوم'
                                      : 'سيتم حساب الرسوم تلقائيًا عند اختيار مجموعة الرسوم وإدخال المبلغ'
                                  }
                                </Typography>
                              </Box>
                            </Grid>
                            
                            {/* عرض تفاصيل الرسوم إذا تم حسابها */}
                            {feeCalculation && (
                              <Grid item xs={12} md={6}>
                                <Box sx={{ border: `1px solid ${alpha(theme.palette.divider, 0.2)}`, borderRadius: 1, p: 2 }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography variant="subtitle2">
                                      تفاصيل الرسوم:
                                    </Typography>
                                    <Typography variant="subtitle2">
                                      {feeCalculation.feeGroupName}
                                    </Typography>
                                  </Box>
                                  
                                  {feeCalculation.feeItems.map((item, index) => (
                                    <Box key={item.feeId} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                      <Typography variant="body2">
                                        {item.feeName}:
                                      </Typography>
                                      <Typography variant="body2">
                                        {isSystemAdmin ? item.amount.toLocaleString() : '***'}
                                      </Typography>
                                    </Box>
                                  ))}
                                  
                                  <Divider sx={{ my: 1 }} />
                                  
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
                                    <Typography variant="subtitle2">
                                      إجمالي الرسوم:
                                    </Typography>
                                    <Typography variant="subtitle2" color="error">
                                      {/* التأكد من عرض المجموع الصحيح في واجهة المستخدم */}
                                      {isSystemAdmin ? (feeCalculation.feeItems.reduce((sum, item) => sum + item.amount, 0)).toLocaleString() : '***'}
                                    </Typography>
                                  </Box>
                                  
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                    <Typography variant="subtitle2">
                                      المبلغ بعد خصم الرسوم:
                                    </Typography>
                                    <Typography variant="subtitle2" color="primary">
                                      {/* حساب المبلغ بعد خصم الرسوم مباشرة هنا مع فحص null */}
                                      {feeCalculation && feeCalculation.feeItems ?
                                        (Number(formik.values.amount) - feeCalculation.feeItems.reduce((sum, item) => sum + item.amount, 0)).toLocaleString()
                                        : formik.values.amount.toLocaleString()}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Grid>
                            )}
                          </Grid>
                          
                          <Box sx={{ mt: 2 }}>
                            <Alert severity="info" icon={<InfoIcon />}>
                              <Typography variant="body2">
                                يتم تطبيق الرسوم على المبلغ المدفوع من الحساب المدين فقط. الحساب الدائن يستلم المبلغ كاملاً.
                              </Typography>
                            </Alert>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}
                  
                  {/* ملخص السند */}
                  <Grid item xs={12}>
                    <Card sx={{ 
                      mb: 3, 
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                    }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          ملخص السند
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <TableContainer>
                              <Table size="small">
                                <TableBody>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>نوع السند</TableCell>
                                    <TableCell>{getVoucherTypeText(template.voucherType)}</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>قالب السند</TableCell>
                                    <TableCell>{template.name}</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>تاريخ السند</TableCell>
                                    <TableCell>
                                      {formik.values.voucherDate ? new Date(formik.values.voucherDate).toLocaleDateString('ar-SA') : ''}
                                    </TableCell>
                                  </TableRow>
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Grid>
                          
                          <Grid item xs={12} md={6}>
                            <TableContainer>
                              <Table size="small">
                                <TableBody>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>المبلغ الأصلي</TableCell>
                                    <TableCell>{Number(formik.values.amount).toLocaleString()}</TableCell>
                                  </TableRow>
                                  {template.hasFees && (
                                    <>
                                      <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold', color: theme.palette.error.main }}>
                                          إجمالي الرسوم
                                        </TableCell>
                                        <TableCell sx={{ color: theme.palette.error.main }}>
                                          {feeCalculation && feeCalculation.feeItems ?
                                            (isSystemAdmin ? (feeCalculation.feeItems.reduce((sum, item) => sum + item.amount, 0)).toLocaleString() : '***')
                                            : '0'}
                                        </TableCell>
                                      </TableRow>
                                      <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                          المبلغ بعد الرسوم
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                          {/* استخدام نفس طريقة الحساب المباشر هنا أيضاً مع فحص null */}
                                          {feeCalculation && feeCalculation.feeItems ?
                                            (Number(formik.values.amount) - feeCalculation.feeItems.reduce((sum, item) => sum + item.amount, 0)).toLocaleString()
                                            : formik.values.amount.toLocaleString()}
                                        </TableCell>
                                      </TableRow>
                                    </>
                                  )}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  {/* أزرار الحفظ والإلغاء */}
                  <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button
                      variant="outlined"
                      color="inherit"
                      onClick={() => navigate(returnPath)}
                      disabled={loading}
                    >
                      إلغاء
                    </Button>
                    
                    {canChangeVoucherStatus && isEditMode && (
                      <Button
                        variant="outlined"
                        color="warning"
                        startIcon={<ArrowForwardIcon />}
                        onClick={() => setStatusChangeDialogOpen(true)}
                        disabled={loading}
                      >
                        تغيير الحالة
                      </Button>
                    )}
                    
                    <Button
                      variant="contained"
                      color="primary"
                      type="submit"
                      startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                      disabled={loading || calculating}
                    >
                      {isEditMode ? 'حفظ التعديلات' : 'إصدار السند'}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </>
          )}
        </>
      )}
      </Paper>
      
      {/* حوار تأكيد تغيير الحالة */}
      <Dialog
        open={statusChangeDialogOpen}
        onClose={() => setStatusChangeDialogOpen(false)}
      >
        <DialogTitle>
          تأكيد تغيير الحالة
        </DialogTitle>
        <DialogContent>
          <Typography>
            {currentVoucher?.isApproved
              ? `هل أنت متأكد من رغبتك في إلغاء اعتماد السند "${currentVoucher?.voucherNumber}"؟`
              : `هل أنت متأكد من رغبتك في اعتماد السند "${currentVoucher?.voucherNumber}"؟`}
          </Typography>
          {currentVoucher?.isApproved && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              إلغاء اعتماد السند سيؤدي إلى عكس تأثيره على أرصدة الحسابات!
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setStatusChangeDialogOpen(false)}
            color="inherit"
            disabled={loading}
          >
            إلغاء
          </Button>
          <Button
            onClick={handleToggleVoucherStatus}
            color="warning"
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'تغيير الحالة'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SpecializedVoucherForm;